import React, { useState, useEffect } from "react";
import "./AdminTools.css";
import "./EventModal.css";
import { api } from "../api/api";
import * as XLSX from "xlsx";

const initialForm = {
  date: "",
  time: "",
  type: "Game",
  homeAway: "Home",
  team: "",
  opponent: "",
  location: "Coker Park",
  pitch: "",
  frequency: "No Repeat",
  notes: "",
  repeatStart: "",
  repeatCount: "",
};

export default function EventModal({
  show,
  onClose,
  onSaved,
  editingEvent,
  teamsList = [],
}) {
  const [form, setForm] = useState(initialForm);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  // ANTI DOPPIO INVIO (D6CAL, 1/10/2026): calco di `invia` in
  // ClinicGuestEnrol.jsx. Si accende prima della chiamata e, se va a buon
  // fine, si spegne solo alla chiusura (dopo gli 800 ms): nell'attesa i
  // pulsanti restano spenti e non si salva due volte.
  const [saving, setSaving] = useState(false);
  const safeTeamsList = Array.isArray(teamsList)
    ? teamsList.map(t => typeof t === "string" ? t : t?.name).filter(Boolean)
    : [];

  useEffect(() => {
    if (editingEvent) {
      setForm({
        ...initialForm,
        ...editingEvent,
        // In modifica la ripetizione NON si offre (D6CAL, decisione del
        // committente 1/10/2026): si modifica solo quell'evento, una serie
        // si crea da «Add Event». Anche un evento nato da serie («Weekly»)
        // si apre su «No Repeat», così il salvataggio è sempre il PUT.
        frequency: "No Repeat",
        result:
          typeof editingEvent.result === "string"
            ? editingEvent.result
            : ""
      });
    } else {
      setForm(initialForm);
    }
  }, [show, editingEvent]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
    setSuccess("");
  };

  function addDays(dateStr, count, freq) {
    const arr = [];
    let curr = new Date(dateStr);
    for (let i = 0; i < count; i++) {
      arr.push(new Date(curr));
      switch (freq) {
        case "Weekly":
          curr.setDate(curr.getDate() + 7);
          break;
        case "Monthly":
          curr.setMonth(curr.getMonth() + 1);
          break;
        case "Daily":
          curr.setDate(curr.getDate() + 1);
          break;
        case "Yearly":
          curr.setFullYear(curr.getFullYear() + 1);
          break;
        default:
          curr.setDate(curr.getDate() + 1);
      }
    }
    return arr;
  }

  // --- IMPORT EXCEL ---

  function excelDateToISO(val) {
  // Se è già una stringa YYYY-MM-DD, la restituisco subito
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
  // Se è una stringa DD/MM/YYYY o DD/MM/YY
  if (typeof val === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(val)) {
    const [d, m, y] = val.split("/");
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  // Se è un numero (seriale Excel)
  if (typeof val === "number") {
    const epoch = new Date(Date.UTC(1899, 11, 30));
    epoch.setDate(epoch.getDate() + val);
    return epoch.toISOString().slice(0, 10);
  }
  // Altrimenti, restituisco stringa vuota
  return "";
}

function excelTimeToString(val) {
  // Se già una stringa tipo "14:00"
  if (typeof val === "string" && /^\d{1,2}:\d{2}/.test(val)) return val;
  // Se è un numero seriale Excel (frazione di giorno)
  if (typeof val === "number") {
    const totalMinutes = Math.round(val * 24 * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  }
  return "";
}

function expandRecurringEvents(ev) {
  if (
    ev.frequency &&
    ev.frequency !== "No Repeat" &&
    ev.repeatStart &&
    ev.repeatCount
  ) {
    const arr = [];
    let curr = new Date(ev.repeatStart);
    for (let i = 0; i < Number(ev.repeatCount); i++) {
      arr.push({
        ...ev,
        date: curr.toISOString().slice(0, 10),
        repeatStart: undefined,
        repeatCount: undefined,
      });
      switch (ev.frequency) {
        case "Weekly":
          curr.setDate(curr.getDate() + 7);
          break;
        case "Monthly":
          curr.setMonth(curr.getMonth() + 1);
          break;
        case "Daily":
          curr.setDate(curr.getDate() + 1);
          break;
        case "Yearly":
          curr.setFullYear(curr.getFullYear() + 1);
          break;
        default:
          curr.setDate(curr.getDate() + 1);
      }
    }
    return arr;
  } else {
    return [ev];
  }
}

function normalizeEvent(ev) {
  return {
    date: excelDateToISO(ev.date || ev.Date || ""),
    time: excelTimeToString(ev.time || ev.Time || ""),
    type: ev.type || ev.Type || "",
    homeAway: ev.homeAway || ev.HomeAway || "",
    team: ev.team || ev.Team || "",
    opponent: ev.opponent || ev.Opponent || "",
    location: ev.location || ev.Location || "",
    pitch: ev.pitch || ev.Pitch || "",
    frequency: ev.frequency || ev.Frequency || "",
    notes: ev.notes || ev.Notes || "",
    repeatStart: excelDateToISO(ev.repeatStart || ev.RepeatStart || ""),
    repeatCount: ev.repeatCount !== undefined && ev.repeatCount !== "" ? Number(ev.repeatCount) : "",
  };
}

  const handleImportExcel = async (e) => {
    setError("");
    setSuccess("");
    const file = e.target.files[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      // Validazione: ogni riga deve avere almeno date, time, type, homeAway, team (case insensitive)
const validRows = json.filter(
  (ev) =>
    (ev.date || ev.Date) &&
    (ev.time || ev.Time) &&
    (ev.type || ev.Type) &&
    (ev.homeAway || ev.HomeAway) &&
    (ev.team || ev.Team)
);

if (validRows.length === 0) {
  setError("No valid events found in file.");
  return;
}

// Normalizza ogni evento per garantire la presenza di tutti i campi, accettando maiuscole/minuscole
const normalizedEvents = validRows.map((ev) =>
  normalizeEvent({
    date: ev.date || ev.Date || "",
    time: ev.time || ev.Time || "",
    type: ev.type || ev.Type || "",
    homeAway: ev.homeAway || ev.HomeAway || "",
    team: ev.team || ev.Team || "",
    opponent: ev.opponent || ev.Opponent || "",
    location: ev.location || ev.Location || "",
    pitch: ev.pitch || ev.Pitch || "",
    frequency: ev.frequency || ev.Frequency || "",
    notes: ev.notes || ev.Notes || "",
    repeatStart: ev.repeatStart || ev.RepeatStart || "",
    repeatCount: ev.repeatCount || ev.RepeatCount || "",
  })
);

// Normalizza e poi espandi gli eventi ricorrenti
let expandedEvents = [];
for (const evRaw of validRows) {
  const ev = normalizeEvent(evRaw);
  expandedEvents = expandedEvents.concat(expandRecurringEvents(ev));
}

await api.post("/events/import", { events: expandedEvents });
setSuccess(`${normalizedEvents.length} events imported!`);
setTimeout(() => {
  setSuccess("");
  if (onSaved) onSaved();
  if (onClose) onClose();
}, 1200);
    } catch (err) {
      setError(
        "Import failed: " +
          (err?.response?.data?.error || "Check file format.")
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setSuccess("");
    // Validazione campi obbligatori
    if (
      !form.date ||
      !form.time ||
      !form.type ||
      !form.homeAway ||
      !form.team ||
      (form.type === "Game" && !form.opponent)
    ) {

      setError("All fields except Location, Pitch and Notes are required. (Opponent required for Game)");
      return;
    }

    if (
      form.frequency !== "No Repeat" &&
      (!form.repeatStart || !form.repeatCount)
    ) {
      setError("Please provide repeat start date and repeat count.");
      return;
    }

    setSaving(true);

    try {
      if (form.frequency === "No Repeat") {
        // Single event
        const saveObj = { ...form };
        delete saveObj.repeatStart;
        delete saveObj.repeatCount;
        if (editingEvent) {
          // La «No Repeat» forzata in modifica non si scrive: il PUT fonde
          // le sole chiavi presenti, e la `frequency` salvata resta com'è.
          delete saveObj.frequency;
          await api.put(`/events/${editingEvent.id}`, saveObj);
          setSuccess("Event updated!");
        } else {
          await api.post("/events", saveObj);
          setSuccess("Event added!");
        }
      } else {
        // Series of events
        const repeatCount = parseInt(form.repeatCount);
        const repeatDates = addDays(
          form.repeatStart,
          repeatCount,
          form.frequency
        );
        const eventsArr = repeatDates.map((d, idx) => ({
          ...form,
          date: d.toISOString().slice(0, 10), // "yyyy-mm-dd"
          repeatStart: undefined,
          repeatCount: undefined,
        }));
        await api.post("/events/series", { events: eventsArr });
        setSuccess(`${repeatCount} events added!`);
      }

      setTimeout(() => {
        setSuccess("");
        setSaving(false);
        if (onSaved) onSaved();
        if (onClose) onClose();
      }, 800);
    } catch (err) {
      setError(
        err?.response?.data?.error || "Error saving event, please try again."
      );
      setSaving(false);
    }
  };

  const handleSaveResult = async () => {
    if (saving) return;
    setError("");
    setSuccess("");
    setSaving(true);
    try {
      await api.patch(`/events/${editingEvent.id}/result`, {
        result: form.result
      });
      setSuccess("Result saved!");
      setTimeout(() => {
        setSuccess("");
        setSaving(false);
        if (onSaved) onSaved();
        if (onClose) onClose();
      }, 800);
    } catch (err) {
      setError("Error saving result: " + (err?.response?.data?.error || ""));
      setSaving(false);
    }
  };

  if (!show) return null;

  // VESTE — CALENDAR ADMIN E2 (27/9/2026): guscio, campi e pulsanti del kit
  // (AdminTools.css), calco della finestra Payments. SOLO LA RESA è cambiata:
  // stato, validazione, invii e rotte sono quelli di prima.
  // ⚠️ Il `<form>` È il guscio (`.qpsc-modal`): così titolo, corpo e comandi
  // restano figli diretti del flex in colonna del kit — il corpo scorre da
  // solo e i comandi, compreso il pulsante `submit`, stanno dentro il form.
  // La «×» in alto non c'è più: si chiude con Cancel, come Payments.
  return (
    <div className="qpsc-modal-overlay">
      <form
        className="qpsc-modal ccl-em-modal"
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <h2 className="qpsc-modal__title">{editingEvent ? "Edit Event" : "Add Event"}</h2>

        {/* Il corpo è l'unica parte che scorre, senza barra visibile. */}
        <div className="qpsc-modal__body">
          {/* Prima riga: Date e Time */}
          <div className="ccl-em-row">
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-date">
                Date<span className="ccl-em-req">*</span>
              </label>
              <input
                id="ccl-em-date"
                className="qpsc-input ccl-em-input"
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
              />
            </div>
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-time">
                Time<span className="ccl-em-req">*</span>
              </label>
              <input
                id="ccl-em-time"
                className="qpsc-input ccl-em-input"
                type="time"
                name="time"
                value={form.time}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Seconda riga: Type e Home/Away */}
          <div className="ccl-em-row">
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-type">
                Type<span className="ccl-em-req">*</span>
              </label>
              <select
                id="ccl-em-type"
                className="qpsc-select"
                name="type"
                value={form.type}
                onChange={handleChange}
                required
              >
                <option value="Game">Game</option>
                <option value="Training">Training</option>
                <option value="Event">Event</option>
              </select>
            </div>
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-homeaway">
                Home/Away<span className="ccl-em-req">*</span>
              </label>
              <select
                id="ccl-em-homeaway"
                className="qpsc-select"
                name="homeAway"
                value={form.homeAway}
                onChange={handleChange}
                required
              >
                <option value="Home">Home</option>
                <option value="Away">Away</option>
                <option value="Bye">Bye</option>
              </select>
            </div>
          </div>

          {/* Terza riga: Team e Opponent */}
          <div className="ccl-em-row">
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-team">
                Team<span className="ccl-em-req">*</span>
              </label>
              <select
                id="ccl-em-team"
                className="qpsc-select"
                name="team"
                value={form.team}
                onChange={handleChange}
                required
              >
                <option value="">Select...</option>
                {safeTeamsList.map((team) => (
                  <option key={team} value={team}>{team}</option>
                ))}
              </select>
            </div>
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-opponent">
                Opponent
                {form.type === "Game" && <span className="ccl-em-req">*</span>}
              </label>
              {/* Spento se il tipo non è Game: lo dice l'opacità 50% del kit
                  (§5 «Stati»), non più uno stile in linea. */}
              <input
                id="ccl-em-opponent"
                className="qpsc-input ccl-em-input"
                type="text"
                name="opponent"
                value={form.opponent}
                onChange={handleChange}
                required={form.type === "Game"}
                placeholder="e.g. vs Ajax"
                disabled={form.type !== "Game"}
              />
            </div>
          </div>

          {/* Campo Result visibile solo se è Game e data <= oggi */}
          {form.type === "Game" && form.date && new Date(form.date) <= new Date() && (
            <div className="ccl-em-row">
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="ccl-em-result">Result</label>
                <input
                  id="ccl-em-result"
                  type="text"
                  name="result"
                  value={form.result || ""}
                  onChange={handleChange}
                  placeholder="e.g. 3-2"
                  className="qpsc-input ccl-em-input"
                />
              </div>
            </div>
          )}

          {/* Quarta riga: Location e Pitch */}
          <div className="ccl-em-row">
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-location">Location</label>
              <input
                id="ccl-em-location"
                className="qpsc-input ccl-em-input"
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                placeholder="e.g. Coker Park"
              />
            </div>
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-pitch">Pitch</label>
              <select
                id="ccl-em-pitch"
                className="qpsc-select"
                name="pitch"
                value={form.pitch}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                <option value="Pitch 1">Pitch 1</option>
                <option value="Pitch 2">Pitch 2</option>
                <option value="Pitch 3">Pitch 3</option>
                <option value="Pitch 4">Pitch 4</option>
                <option value="Pitch 5">Pitch 5</option>
                <option value="Pitch 6">Pitch 6</option>
                <option value="Pitch 7">Pitch 7</option>
                <option value="Pitch 8">Pitch 8</option>
                <option value="Pitch 9">Pitch 9</option>
                <option value="NA">NA</option>
              </select>
            </div>
          </div>

          {/* Quinta riga: Frequency, Notes, Repeat period.
              In modifica Frequency non si rende (D6CAL): resta solo Notes. */}
          <div className="ccl-em-row">
            {!editingEvent && (
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="ccl-em-frequency">
                  Frequency<span className="ccl-em-req">*</span>
                </label>
                <select
                  id="ccl-em-frequency"
                  className="qpsc-select"
                  name="frequency"
                  value={form.frequency}
                  onChange={handleChange}
                  required
                >
                  <option value="No Repeat">No Repeat</option>
                  <option value="Weekly">Weekly</option>
                  <option value="Monthly">Monthly</option>
                  <option value="Daily">Daily</option>
                  <option value="Yearly">Yearly</option>
                </select>
              </div>
            )}
            <div className="qpsc-field">
              <label className="qpsc-field__label" htmlFor="ccl-em-notes">Notes</label>
              <input
                id="ccl-em-notes"
                className="qpsc-input ccl-em-input"
                type="text"
                name="notes"
                value={form.notes}
                onChange={handleChange}
                placeholder="Notes"
              />
            </div>
          </div>

          {/* SOLO se Frequency è diversa da No Repeat, e mai in modifica */}
          {!editingEvent && form.frequency !== "No Repeat" && (
            <div className="ccl-em-row">
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="ccl-em-repeat-start">
                  Repeat Start Date<span className="ccl-em-req">*</span>
                </label>
                <input
                  id="ccl-em-repeat-start"
                  className="qpsc-input ccl-em-input"
                  type="date"
                  name="repeatStart"
                  value={form.repeatStart || ""}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="qpsc-field">
                <label className="qpsc-field__label" htmlFor="ccl-em-repeat-count">
                  Repeat Count<span className="ccl-em-req">*</span>
                </label>
                <input
                  id="ccl-em-repeat-count"
                  className="qpsc-input ccl-em-input"
                  type="number"
                  name="repeatCount"
                  min={1}
                  value={form.repeatCount || ""}
                  onChange={handleChange}
                  required
                  placeholder="How many times?"
                />
              </div>
            </div>
          )}
        </div>

        {/* Messaggi in finestra, fuori dal corpo che scorre: restano in vista
            sopra i comandi anche con la finestra piena. */}
        {error && <div className="event-modal-error" role="alert">{error}</div>}
        {success && <div className="event-modal-success" role="status">{success}</div>}

        <div className="qpsc-modal__actions">
          <button type="submit" className="qpsc-tool-btn" disabled={saving}>
            {saving ? "Saving..." : editingEvent ? "Save Changes" : "Add Event"}
          </button>

          {editingEvent &&
            form.type === "Game" &&
            form.date &&
            new Date(form.date) <= new Date() && (
              <button
                type="button"
                className="qpsc-tool-btn"
                onClick={handleSaveResult}
                disabled={saving}
              >
                Save Result
              </button>
          )}

          {/* --- IMPORT EXCEL BUTTON --- NASCOSTO 27/9/2026 (decisione del
              committente, cantiere OUTLOOK, incarico EO2 parte B): si nasconde
              il comando, NON si elimina il codice. handleImportExcel e le sue
              funzioni restano in file, orfane; la rotta POST /events/import
              resta. Riaccendere ripristinando il bottone qui sotto:
          <label className="qpsc-ghost-btn">
            Import Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleImportExcel}
            />
          </label>
          */}

          <button
            type="button"
            className="qpsc-ghost-btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}